import React from 'react'
import dayjs from 'dayjs'

export function membersToOptions (members) {
  if (!members?.length) return []
  return members.map(m => ({
    label: m.full_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || `Contact ${m.contact_id}`,
    value: m.contact_id ?? m.id,
    hoh: !!m.hoh
  }))
}

export function getWorkflowStatus (workflow) {
  if (workflow.completion_date) return 'complete'
  if (!workflow.due_date) return 'outstanding'
  return dayjs(workflow.due_date).isBefore(dayjs(), 'day') ? 'overdue' : 'outstanding'
}

export const labelStyle = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-color-subtle)',
  marginBottom: '2px'
}

export const valueStyle = {
  fontSize: 'var(--font-size-sm)',
  marginBottom: '8px'
}

export const editorBorderStyle = {
  border: 'var(--border-neutral-thin)',
  borderRadius: 'var(--border-radius-sm)',
  overflow: 'hidden'
}

export function renderContactOption (option) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <span>{option.label}</span>
      {option.hoh ? (
        <span style={{ fontSize: '11px', color: 'var(--text-color-subtle)' }}>
          Head of Household
        </span>
      ) : null}
    </div>
  )
}

export const RECURRENCE_OPTIONS = [
  { label: 'Never', value: 'never' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' }
]

export const DAYS_OF_WEEK = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
]

export const ORDINALS = [
  { label: 'First', value: 1 },
  { label: 'Second', value: 2 },
  { label: 'Third', value: 3 },
  { label: 'Fourth', value: 4 },
  { label: 'Last', value: -1 }
]

export const MONTHS_OF_YEAR = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
]

export const CUSTOM_VALUE = '__custom__'

export const RECURRENCE_OPTIONS_WITH_CUSTOM = [
  ...RECURRENCE_OPTIONS,
  { label: 'Custom\u2026', value: CUSTOM_VALUE }
]

export function defaultRecurrenceState (baseType = 'daily', startDate) {
  const d = startDate ? dayjs(startDate) : dayjs()
  const dayIndex = d.isValid() ? d.day() : 1
  const dayName = DAYS_OF_WEEK[dayIndex]
  return {
    baseType,
    repeats_every_n_days: 1,
    repeats_every_n_weeks: 1,
    repeats_weekly_each_days_of_the_week: dayName ? [dayName] : ['monday'],
    repeats_every_n_months: 1,
    repeats_monthly: 'on',
    repeats_monthly_each_days_of_the_month: d.isValid() ? [d.date()] : [1],
    repeats_monthly_on_ordinals: [Math.ceil((d.isValid() ? d.date() : 1) / 7)],
    repeats_monthly_on_days_of_the_week: dayName ? [dayName] : ['monday'],
    repeats_every_n_years: 1,
    repeats_yearly_each_months_of_the_year: d.isValid()
      ? [MONTHS_OF_YEAR[d.month()]]
      : ['january'],
    repeats_yearly_on: false,
    repeats_yearly_on_ordinals: [1],
    repeats_yearly_on_days_of_the_week: dayName ? [dayName] : ['monday'],
    repeat_ends: 'never',
    repeat_ends_on: ''
  }
}

export function serializeRecurrence (recurrenceState) {
  if (!recurrenceState) return { repeats: 'never' }

  const { baseType } = recurrenceState
  const result = { repeats: baseType }

  switch (baseType) {
    case 'daily':
      result.repeats_every_n_days = recurrenceState.repeats_every_n_days || 1
      break
    case 'weekly':
      result.repeats_every_n_weeks = recurrenceState.repeats_every_n_weeks || 1
      if (recurrenceState.repeats_weekly_each_days_of_the_week?.length) {
        result.repeats_weekly_each_days_of_the_week =
          recurrenceState.repeats_weekly_each_days_of_the_week
      }
      break
    case 'monthly':
      result.repeats_every_n_months = recurrenceState.repeats_every_n_months || 1
      result.repeats_monthly = recurrenceState.repeats_monthly || 'on'
      if (recurrenceState.repeats_monthly === 'each') {
        if (recurrenceState.repeats_monthly_each_days_of_the_month?.length) {
          result.repeats_monthly_each_days_of_the_month =
            recurrenceState.repeats_monthly_each_days_of_the_month
        }
      } else {
        if (recurrenceState.repeats_monthly_on_ordinals?.length) {
          result.repeats_monthly_on_ordinals =
            recurrenceState.repeats_monthly_on_ordinals
        }
        if (recurrenceState.repeats_monthly_on_days_of_the_week?.length) {
          result.repeats_monthly_on_days_of_the_week =
            recurrenceState.repeats_monthly_on_days_of_the_week
        }
      }
      break
    case 'yearly':
      result.repeats_every_n_years = recurrenceState.repeats_every_n_years || 1
      if (recurrenceState.repeats_yearly_each_months_of_the_year?.length) {
        result.repeats_yearly_each_months_of_the_year =
          recurrenceState.repeats_yearly_each_months_of_the_year
      }
      if (recurrenceState.repeats_yearly_on) {
        result.repeats_yearly_on = true
        if (recurrenceState.repeats_yearly_on_ordinals?.length) {
          result.repeats_yearly_on_ordinals =
            recurrenceState.repeats_yearly_on_ordinals
        }
        if (recurrenceState.repeats_yearly_on_days_of_the_week?.length) {
          result.repeats_yearly_on_days_of_the_week =
            recurrenceState.repeats_yearly_on_days_of_the_week
        }
      }
      break
    default:
      break
  }

  if (recurrenceState.repeat_ends === 'on' && recurrenceState.repeat_ends_on) {
    result.repeat_ends = 'on'
    result.repeat_ends_on = recurrenceState.repeat_ends_on
  }

  return result
}

// TODO: Custom recurrences (e.g., "Every 2 weeks on Mon/Wed") currently display as
// just their base type ("Weekly"). Enhance to build a human-readable description
// from the detailed recurrence fields when they are present.
export function describeRecurrence (activity) {
  const r = activity?.repeats
  if (!r || r === 'never') return null

  const map = {
    'simple-daily': 'Every day',
    daily: 'Daily',
    'every-weekday': 'Every weekday (Mon\u2013Fri)',
    'weekly-on-day-of-week': 'Weekly',
    weekly: 'Weekly',
    'monthly-on-nth': 'Monthly',
    'monthly-on-last-weekday': 'Monthly',
    'last-day-of-month': 'Monthly (last day)',
    monthly: 'Monthly',
    'annually-on-date': 'Yearly',
    yearly: 'Yearly'
  }
  return map[r] || r
}

export const PRIORITY_OPTIONS = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 }
]

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'TABLE', 'BLOCKQUOTE', 'PRE', 'HR', 'LI'
])

function ensureBlockStructure (html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  let child = body.firstChild
  while (child) {
    if (child.nodeType === 1 && BLOCK_TAGS.has(child.tagName)) {
      child = child.nextSibling
      continue
    }
    const p = doc.createElement('p')
    body.insertBefore(p, child)
    while (child && !(child.nodeType === 1 && BLOCK_TAGS.has(child.tagName))) {
      const next = child.nextSibling
      if (child.nodeType === 1 && child.tagName === 'BR') {
        child.remove()
        child = next
        break
      }
      p.appendChild(child)
      child = next
    }
    if (!p.hasChildNodes()) p.remove()
  }
  return body.innerHTML
}

export function normalizeRedtailHtml (str) {
  if (!str || typeof str !== 'string') return str
  const unescaped = str
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
  return ensureBlockStructure(unescaped)
}

export function getTaskStatus (activity) {
  if (activity.completed) return 'completed'
  const dueDate = dayjs(activity.end_date)
  if (dueDate.isBefore(dayjs(), 'day')) return 'overdue'
  return 'outstanding'
}

export function extractPriorityOptions (activities) {
  if (!activities?.length) return PRIORITY_OPTIONS
  const seen = new Set()
  const options = []
  for (const a of activities) {
    if (a.priority != null && a.priority !== '' && !seen.has(a.priority)) {
      seen.add(a.priority)
      options.push({ label: String(a.priority), value: a.priority })
    }
  }
  return options.length ? options : PRIORITY_OPTIONS
}
